// Order drill-in for the shop floor: everything going to one customer order.
// Per line — ordered / shipped / allocated (covered from stock) / outstanding,
// and the v2 production picture (top WO, produced, remaining).

import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Table, TableBody, TableCell, TableHead,
  TableRow, Typography,
} from '@mui/material';
import { CheckCircle, ShoppingCart, Warning } from '@mui/icons-material';

import { v2 } from '../../../../services/api';
import { orderStatusColor, orderStatusLabel } from '../utils/orderStatus';

function ProductionCell({ line }) {
  if (line.is_covered) {
    return (
      <Chip
        label="Covered"
        size="small"
        color="success"
        icon={<CheckCircle />}
      />
    );
  }
  if (line.v2?.has_tree) {
    const produced = parseFloat(line.v2.produced) || 0;
    const toMake = parseFloat(line.v2.quantity_to_produce) || 0;
    return (
      <Box>
        <Typography variant="body2" fontWeight="bold">
          {line.v2.top_wo_number}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {produced} of {toMake} made
          {line.v2.wo_count > 1 ? ` · ${line.v2.wo_count} WOs in tree` : ''}
        </Typography>
      </Box>
    );
  }
  return (
    <Chip
      label="No work orders yet"
      size="small"
      color="warning"
      icon={<Warning />}
      variant="outlined"
    />
  );
}

export default function OrderSummaryDialogV2({ open, orderId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!open || orderId == null) return undefined;
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(false);
      setData(null);
      try {
        const res = await v2.customerOrders.summary(orderId);
        if (!cancelled) setData(res.data);
      } catch (err) {
        console.error('Order summary failed', err);
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [open, orderId]);

  const order = data?.order;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth data-testid="order-summary-dialog">
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <ShoppingCart />
          {order ? (
            <>
              <Typography variant="h6" component="span" fontWeight="bold">
                {order.order_number}
              </Typography>
              <Typography variant="body1" component="span">
                {order.customer_name}
              </Typography>
              <Chip
                label={orderStatusLabel(order.status)}
                color={orderStatusColor(order.status)}
                size="small"
              />
              {order.due_date && (
                <Typography variant="body2" color="text.secondary" component="span">
                  due {new Date(order.due_date).toLocaleDateString()}
                </Typography>
              )}
            </>
          ) : 'Customer Order'}
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        )}
        {error && (
          <Alert severity="error" data-testid="order-summary-error">
            Failed to load the order summary.
          </Alert>
        )}
        {data && !loading && (
          <Table size="small" data-testid="order-summary-table">
            <TableHead>
              <TableRow>
                <TableCell>Line</TableCell>
                <TableCell>Product</TableCell>
                <TableCell align="right">Ordered</TableCell>
                <TableCell align="right">Shipped</TableCell>
                <TableCell align="right">Allocated</TableCell>
                <TableCell align="right">Still needed</TableCell>
                <TableCell>Production</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.lines.map((l) => (
                <TableRow
                  key={l.line_id}
                  data-testid={`order-line-${l.line_id}`}
                  sx={{ backgroundColor: l.is_covered ? '#e8f5e9' : 'transparent' }}
                >
                  <TableCell>{l.line_number}</TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">{l.product_name}</Typography>
                    <Typography variant="caption" color="text.secondary">{l.product_code}</Typography>
                  </TableCell>
                  <TableCell align="right">{parseFloat(l.quantity_ordered)}</TableCell>
                  <TableCell align="right">{parseFloat(l.quantity_shipped)}</TableCell>
                  <TableCell align="right">{parseFloat(l.quantity_allocated)}</TableCell>
                  <TableCell align="right">
                    <Typography
                      variant="body2"
                      fontWeight="bold"
                      color={parseFloat(l.quantity_outstanding) > 0 ? 'error.main' : 'success.main'}
                    >
                      {parseFloat(l.quantity_outstanding)}
                    </Typography>
                  </TableCell>
                  <TableCell><ProductionCell line={l} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained" data-testid="order-summary-close">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
